import React, { JSX, useState } from 'react';
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  useMediaQuery,
  SxProps,
  useTheme,
} from '@mui/material';
import ResetIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';

interface DeleteConfirmationProps {
  // Legacy props for backward compatibility (uncontrolled mode)
  onDelete?: () => void;
  disabled?: boolean;
  label?: string;
  action?: 'delete' | 'reset';
  color?:
    | 'inherit'
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | undefined;
  sx?: SxProps;
  // New props for controlled mode
  open?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  // Optional props for button customization in controlled mode
  hideButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const DeleteConfirmation = (props: DeleteConfirmationProps): JSX.Element => {
  const {
    // Legacy props
    onDelete,
    disabled,
    label,
    color,
    action = 'delete',
    // New props
    open: controlledOpen,
    onClose: controlledOnClose,
    onConfirm,
    title,
    message,
    hideButton = false,
    confirmButtonText,
    cancelButtonText = 'Cancel',
    size = 'large',
    sx,
    icon = props.action === 'reset' ? <ResetIcon /> : <DeleteIcon />,
  } = props;

  // Internal state for uncontrolled mode
  const [internalOpen, setInternalOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleClickOpen = (): void => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  };

  const handleClose = (): void => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleConfirm = (): void => {
    if (isControlled) {
      onConfirm?.();
    } else {
      onDelete?.();
      setInternalOpen(false);
    }
  };

  // Determine dialog content based on mode
  const dialogTitle = title || 'Confirm Reset';
  const dialogMessage =
    message ||
    `This action will permanently ${capitalizeFirstLetter(action)} ${
      label ? label.toLowerCase() : 'all data'
    } without the ability to recover it. Are you sure you want to continue?`;
  const confirmText =
    confirmButtonText || `${capitalizeFirstLetter(action)} ${label || 'Everything'}`;

  return (
    <>
      {/* Only show button if not hidden (for controlled mode) */}
      {!hideButton && (
        <IconButton
          aria-label={action}
          onClick={(e): void => {
            e.stopPropagation();
            e.preventDefault();
            handleClickOpen();
          }}
          title={label ? `${capitalizeFirstLetter(action)} ${label}` : 'Reset'}
          color={color || 'default'}
          sx={{ display: 'flex', margin: 'auto 0px', ...sx }}
          size={size}
          edge="start"
          disabled={disabled}
        >
          {icon}
        </IconButton>
      )}

      <Dialog
        fullScreen={fullScreen}
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            {cancelButtonText}
          </Button>
          <Button onClick={handleConfirm} color="error" variant="contained">
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { DeleteConfirmation };
